import { Dayjs } from 'dayjs';
import { Duration } from 'dayjs/plugin/duration';

import { FileContentType, isString, SDate, SDuration } from '@summit/core';
import { IUserExperience, TimeRange, Week } from '@summit/models';

import { HourlyWorkType } from '@app/models/ebo/digital-logs/hourly-work-type.enum';
import { ILeg } from '@app/models/ebo/digital-logs/i-leg';
import { RerouteReason } from '@app/models/ebo/digital-logs/reroute-reason.enum';
import { EboEmployeeEvent } from '../../base-classes/ebo-employee-event';
import { Employee } from '../../employee/employee';
import { ServiceArea } from '../../organization/service-area/service-area';
import { PayType } from '../../payroll/shared/pay-type.enum';
import { Hub } from '../../places/hub/hub';
import { Reconcile } from '../../reconcile/reconcile';
import { Route } from '../../route/route';
import { DigitalLogType } from '../../shared/digital-log-type.enum';
import { SpotRun } from '../../spot/spot-run';
import { TruckAlias } from '../../truck/truck-alias';
import { GpsLeg } from '../gps/gps-leg';
import { AskFile } from './ask-file';

export class Ask extends EboEmployeeEvent implements ILeg {
  //fields specific to each type of trip
  static readonly allFields: (keyof Ask)[] = [
    'eventDate',
    'employeeId',
    'logType',
    'truckAliasId',
    'inProgress',
    'comment'
  ];
  static readonly lhFields: (keyof Ask)[] = [];
  static readonly spotFields: (keyof Ask)[] = [];
  static readonly pnDFields: (keyof Ask)[] = [];
  static readonly hourlyFields: (keyof Ask)[] = ['hourlyWorkType', 'startTime', 'origNum', 'duration', 'endTime'];
  static readonly dayFields: (keyof Ask)[] = ['hourlyWorkType', 'startTime', 'origNum', 'endTime'];

  //Tracking
  logDate: Dayjs;
  createdOn: Dayjs;
  createdById: string;
  createdBy: Employee;
  //High Level
  logType: DigitalLogType;
  hourlyWorkType: HourlyWorkType;
  truckAliasId: string;
  truckAlias: TruckAlias;
  inProgress: boolean;
  //pre Departure
  startTime: Dayjs;
  eldIssue: boolean;
  paperLogsRequired: boolean;
  eldIssueComment: string;
  //Trip Info
  payTypeID: PayType;
  origNum: number;
  origHub: Hub;
  destNum: number;
  destHub: Hub;
  spotRunId: string;
  spotRun: SpotRun;
  //Begin/Departure
  odometerBegin: number;
  departureTime: Dayjs;
  departureDelay: Duration;
  routeID: string;
  route: Route;
  serviceAreaId: string;
  serviceArea: ServiceArea;
  //Load Info
  trailer1: string;
  trailer1Seal: string;
  trailer2Dolly: string;
  trailer2: string;
  trailer2Seal: string;
  trailer3Dolly: string;
  trailer3: string;
  trailer3Seal: string;
  returnTrailer1: string;
  returnTrailer1Seal: string;
  returnTrailer2Dolly: string;
  returnTrailer2: string;
  returnTrailer2Seal: string;
  returnTrailer3Dolly: string;
  returnTrailer3: string;
  returnTrailer3Seal: string;

  liveLoad: boolean; //spot specific
  packagePickupCount: number;
  stopPickupCount: number;
  packageDeliveredCount: number;
  stopDeliveredCount: number;
  //Arrival/Finish
  arrivalTime: Dayjs;
  odometerEnd: number;
  rerouted: boolean;
  rerouteReason: RerouteReason;
  inRouteDelay: Duration;
  //Post Finish
  endTime: Dayjs;
  duration: Duration;

  //Wizard tracking
  wizardStep: string;
  trailerCount: number;
  returnTrailerCount: number;
  tractorDamage: boolean;
  trailer1Damage: boolean;
  trailer2Damage: boolean;
  trailer3Damage: boolean;
  returnTrailer1Damage: boolean;
  returnTrailer2Damage: boolean;
  returnTrailer3Damage: boolean;
  trailer1AtDock: boolean;
  trailer2AtDock: boolean;
  trailer3AtDock: boolean;
  returnTrailer1AtDock: boolean;
  returnTrailer2AtDock: boolean;
  returnTrailer3AtDock: boolean;
  dvirReport: boolean;

  //Shift Tracking
  startOfShift: boolean;
  endOfShift: boolean;

  //yet to be classified
  miles: number;
  run?: number;
  comment: string;
  gpsLegs: GpsLeg[];
  pending: boolean;
  //Files
  attachedContentTypes: FileContentType[]; //used for detail summary without pulling file list
  files: AskFile[];

  reconcileID?: string;
  reconcile: Reconcile;

  // Info for new/continue Trips
  truckNum: string;
  addRoundTrip?: boolean;
  addRoundTripDate?: Dayjs;
  addDelegate?: boolean;
  coDriverId?: string;
  coDriver?: Employee;
  addMiscWork?: boolean;
  addMiscWorkAmount?: number;
  addMiscWorkComment?: string;
  startNextTrip?: boolean;
  nextLogType?: DigitalLogType;

  static SortByTravel(inList: Ask[], startHub: number): Ask[] {
    if (inList.length <= 1) return inList;
    if (inList.length == 2) {
      if (inList[0].origNum == startHub) return inList;
      return inList.reverse();
    }
    const inputList = [...inList];
    let responseList = new Array<Ask>();
    let nextLegIndex = inputList.findIndex((entry) => entry.origNum == startHub);
    if (nextLegIndex < 0) nextLegIndex = 0;
    const nextLegs = inputList.splice(nextLegIndex, 1);
    responseList.push(nextLegs[0]);
    const remainingLegs = Ask.SortByTravel(inputList, nextLegs[0].destNum);
    responseList = responseList.concat(remainingLegs);
    return responseList;
  }
  constructor(jsonData?: Partial<Ask>) {
    super(jsonData);
    if (jsonData) {
      if (jsonData.logDate) this.logDate = SDate.newUtc(jsonData.logDate);
      if (jsonData.createdOn) this.createdOn = SDate.newUtc(jsonData.createdOn);
      if (jsonData.createdBy) this.createdBy = new Employee(jsonData.createdBy);
      if (isString(this.logType)) this.logType = parseInt(this.logType);
      if (isString(this.hourlyWorkType)) this.hourlyWorkType = parseInt(this.hourlyWorkType);
      if (jsonData.truckAlias) {
        this.truckAlias = new TruckAlias(jsonData.truckAlias);
        this.truckNum = this.truckAlias.name;
      }
      if (jsonData.startTime) this.startTime = SDate.newUtc(jsonData.startTime);
      if (isString(this.payTypeID)) this.payTypeID = parseInt(this.payTypeID);
      if (jsonData.coDriver) this.coDriver = new Employee(jsonData.coDriver);
      if (jsonData.origHub) this.origHub = new Hub(jsonData.origHub);
      if (jsonData.destHub) this.destHub = new Hub(jsonData.destHub);
      if (jsonData.spotRun) this.spotRun = new SpotRun(jsonData.spotRun);
      if (jsonData.serviceArea) this.serviceArea = new ServiceArea(jsonData.serviceArea);

      if (jsonData.departureTime) this.departureTime = SDate.newUtc(jsonData.departureTime);
      if (jsonData.departureDelay) this.departureDelay = SDuration.new(jsonData.departureDelay);
      if (jsonData.route) this.route = new Route(jsonData.route);

      if (jsonData.arrivalTime) this.arrivalTime = SDate.newUtc(jsonData.arrivalTime);
      if ((this.rerouted as unknown as string) == '') this.rerouted = false;
      if (jsonData.inRouteDelay) this.inRouteDelay = SDuration.new(jsonData.inRouteDelay);

      if (jsonData.endTime) this.endTime = SDate.newUtc(jsonData.endTime);
      if (jsonData.duration) this.duration = SDuration.new(jsonData.duration);

      if (jsonData.reconcile) this.reconcile = new Reconcile(jsonData.reconcile);
      if (jsonData.gpsLegs) this.gpsLegs = jsonData.gpsLegs.map((item) => new GpsLeg(item));
      if (jsonData.files) this.files = jsonData.files.map((item) => new AskFile(item));
    }
    this.gpsLegs ??= [];
    this.rerouted ??= false;
    this.logType ??= DigitalLogType.FedExLinehaul;
    this.rerouteReason ??= RerouteReason.NotRerouted;
    this.hourlyWorkType ??= HourlyWorkType.General;
    this.liveLoad ??= false;
    this.inProgress ??= false;
    this.eldIssue ??= false;
    this.paperLogsRequired ??= false;
    this.attachedContentTypes ??= [];
    this.files ??= [];
    if (this.files.length > 0) {
      if (this.files.findIndex((f) => f.contentType == FileContentType.TractorDamage) >= 0) {
        this.tractorDamage = true;
      }
      if (this.files.findIndex((f) => f.contentType == FileContentType.Trailer1Defects) >= 0) {
        this.trailer1Damage = true;
      }
      if (this.files.findIndex((f) => f.contentType == FileContentType.Trailer2Defects) >= 0) {
        this.trailer2Damage = true;
      }
      if (this.files.findIndex((f) => f.contentType == FileContentType.Trailer3Defects) >= 0) {
        this.trailer3Damage = true;
      }
      if (this.files.findIndex((f) => f.contentType == FileContentType.ReturnTrailer1Defects) >= 0) {
        this.returnTrailer1Damage = true;
      }
      if (this.files.findIndex((f) => f.contentType == FileContentType.ReturnTrailer2Defects) >= 0) {
        this.returnTrailer2Damage = true;
      }
      if (this.files.findIndex((f) => f.contentType == FileContentType.ReturnTrailer3Defects) >= 0) {
        this.returnTrailer3Damage = true;
      }
      if (this.files.findIndex((f) => f.contentType == FileContentType.Trailer1DockDoor) >= 0) {
        this.trailer1AtDock = true;
      }
      if (this.files.findIndex((f) => f.contentType == FileContentType.Trailer2DockDoor) >= 0) {
        this.trailer2AtDock = true;
      }
      if (this.files.findIndex((f) => f.contentType == FileContentType.Trailer3DockDoor) >= 0) {
        this.trailer3AtDock = true;
      }
      if (this.files.findIndex((f) => f.contentType == FileContentType.ReturnTrailer1DockDoor) >= 0) {
        this.returnTrailer1AtDock = true;
      }
      if (this.files.findIndex((f) => f.contentType == FileContentType.ReturnTrailer2DockDoor) >= 0) {
        this.returnTrailer2AtDock = true;
      }
      if (this.files.findIndex((f) => f.contentType == FileContentType.ReturnTrailer3DockDoor) >= 0) {
        this.returnTrailer3AtDock = true;
      }

      if (this.files.findIndex((f) => f.contentType == FileContentType.DVIR) >= 0) {
        this.dvirReport = true;
      }
    }
    //console.log(`[Trip] Ask: ${this.id}`, jsonData, this);
  }

  get hasGps(): boolean {
    return this.gpsLegs?.length > 0;
  }

  get isDelayedDeparture(): boolean {
    return this.departureDelay ? true : false;
  }
  set isDelayedDeparture(_: boolean) {
    //NOP
  }

  get origAbbr(): string {
    switch (this.logType) {
      case DigitalLogType.Break:
      case DigitalLogType.TimeClock:
        return this.startTime?.local().format('M/D hh:mm A') ?? '--';
      case DigitalLogType.FedExLinehaul:
        return this.origNum?.toString() ?? '--';
      case DigitalLogType.Hourly:
        if (this.hourlyWorkType == HourlyWorkType.ELD) {
          return this.startTime?.local().format('M/D hh:mm A') ?? '--';
        } else {
          return this.origNum?.toString() ?? '--';
        }
      case DigitalLogType.FedExSpot:
        return this.spotRun ? `#${this.spotRun.spotNumber}` : '--';
      default:
        return '--';
    }
  }
  set origAbbr(_: string) {
    //NOP
  }

  get destAbbr(): string {
    switch (this.logType) {
      case DigitalLogType.Break:
      case DigitalLogType.TimeClock:
        return this.inProgress ? 'Active' : (this.endTime?.local().format('M/D hh:mm A') ?? '--');
      case DigitalLogType.FedExLinehaul:
        return this.destNum?.toString() ?? '--';
      case DigitalLogType.FedExSpot:
        return this.spotRun ? `#${this.spotRun.spotNumber}` : '--';
      case DigitalLogType.Hourly:
        if (this.hourlyWorkType == HourlyWorkType.ELD) {
          return this.endTime?.local().format('M/D hh:mm A') ?? '--';
        } else {
          return '--';
        }
      default:
        return '--';
    }
  }
  set destAbbr(_: string) {
    //NOP
  }

  isEditable(ux: IUserExperience): boolean {
    const editable = !this.reconcileID && new Week(this.eventDate).isEditable(ux);
    //console.log(`Editable: ${editable}`);
    return editable;
  }

  toShortString(): string {
    let response = this.eventDate ? this.eventDate.format('M/D/YY') : 'Unknown';
    if (this.run) {
      response += `R${this.run}`;
    }
    response += `- ${this.employee ? this.employee?.fedExID : 'Unknown'} - ${this.origNum}->${this.destNum} = ${
      this.miles
    }`;
    return response;
  }

  getNextLeg(): Partial<Ask> {
    const newTrip = new Object() as Ask;
    newTrip.eventDate = this.eventDate;
    newTrip.employeeID = this.employeeID;
    newTrip.truckNum = this.truckNum;
    newTrip.origNum = this.destNum;
    newTrip.destNum = this.origNum;
    newTrip.miles = this.miles;
    newTrip.payTypeID = this.payTypeID;
    newTrip.routeID = this.routeID;
    if (this.odometerEnd) newTrip.odometerBegin = this.odometerEnd;
    return newTrip;
  }

  getTimeWindow(): Partial<TimeRange> {
    return {
      beginTime: SDate.new(this.eventDate).add(-12, 'hours'),
      endTime: SDate.new(this.eventDate).add(82, 'hours') //36 normal + 2 days for Lost connection
    };
  }
}
